import React, { useState, useEffect, useRef, useMemo } from 'react';
import QRCode from 'qrcode';
import { subscribeToUserCollection } from '../utils/userDataHelper';
import { getHindiName } from './utils/hindiNames';
import { normalizeRecipe, additiveGrams } from '../utils/recipeSchema';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/labels-print.css';

/**
 * LabelGenerator - Create product labels with QR codes
 * Route: /lab/labels
 */

// Real QR modules with a four-module quiet zone, encoded synchronously for print.
const RecipeQRCode = ({ data, size = 100 }) => {
  const qr = useMemo(() => {
    try { return QRCode.create(data, { errorCorrectionLevel: 'M' }); }
    catch { return null; }
  }, [data]);
  if (!qr) return <span role="alert">QR data is too long.</span>;
  const count = qr.modules.size;
  const quiet = 4;
  const path = [];
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (qr.modules.get(row, col)) path.push(`M${col + quiet},${row + quiet}h1v1h-1z`);
    }
  }
  return <svg width={size} height={size} viewBox={`0 0 ${count + quiet * 2} ${count + quiet * 2}`} role="img" aria-label="Product and batch QR code" shapeRendering="crispEdges"><rect width="100%" height="100%" fill="white" /><path d={path.join('')} fill="black" /></svg>;
};

// Ingredient list for labels, in descending order of weight (how cosmetic labels list
// ingredients). Lye is listed only when the recipe used it; melt-and-pour has none.
const INCI_NAMES = {
  olive_oil: 'Olea Europaea (Olive) Fruit Oil',
  coconut_oil: 'Cocos Nucifera (Coconut) Oil',
  palm_oil: 'Elaeis Guineensis (Palm) Oil',
  castor_oil: 'Ricinus Communis (Castor) Seed Oil',
  shea_butter: 'Butyrospermum Parkii (Shea) Butter',
  cocoa_butter: 'Theobroma Cacao (Cocoa) Seed Butter',
  rice_bran_oil: 'Oryza Sativa (Rice) Bran Oil',
  sunflower_oil: 'Helianthus Annuus (Sunflower) Seed Oil',
  almond_oil: 'Prunus Amygdalus Dulcis (Sweet Almond) Oil',
  sweet_almond_oil: 'Prunus Amygdalus Dulcis (Sweet Almond) Oil',
  sesame_oil: 'Sesamum Indicum (Sesame) Seed Oil',
  neem_oil: 'Azadirachta Indica (Neem) Seed Oil',
  jojoba_oil: 'Simmondsia Chinensis (Jojoba) Seed Oil',
  avocado_oil: 'Persea Gratissima (Avocado) Oil',
  turmeric: 'Curcuma Longa (Turmeric) Root Extract',
  honey: 'Mel (Honey)',
  aloe_vera: 'Aloe Barbadensis Leaf Juice',
  activated_charcoal: 'Charcoal Powder',
  kaolin_clay: 'Kaolin',
  lavender: 'Lavandula Angustifolia (Lavender) Oil',
  tea_tree: 'Melaleuca Alternifolia (Tea Tree) Leaf Oil',
  peppermint: 'Mentha Piperita (Peppermint) Oil',
  sandalwood: 'Santalum Album (Sandalwood) Oil',
  rose: 'Rosa Damascena Flower Oil',
};

const inciKey = item => (item.key || item.name || '').toLowerCase().replace(/\s+/g, '_');

export const formatINCI = (input = {}) => {
  const recipe = normalizeRecipe(input);
  const entries = [];
  const add = (name, grams) => {
    if (!name) return;
    const weight = Number.isFinite(Number(grams)) ? Math.max(0, Number(grams)) : 0;
    const existing = entries.find(entry => entry.name === name);
    if (existing) existing.grams += weight; else entries.push({ name, grams: weight });
  };
  recipe.oils.forEach(oil => add(INCI_NAMES[inciKey(oil)] || oil.name, oil.weight));
  if (recipe.recipeType !== 'melt_pour') {
    if (recipe.waterWeight > 0) add('Aqua', recipe.waterWeight);
    const naoh = Number(recipe.alkaliWeights?.NaOH) || 0;
    const koh = Number(recipe.alkaliWeights?.KOH) || 0;
    if (naoh > 0) add('Sodium Hydroxide', naoh);
    if (koh > 0) add('Potassium Hydroxide', koh);
    // Older records without alkali amounts still name the alkali they were made with.
    if (!(naoh > 0) && !(koh > 0)) add(recipe.lyeType === 'KOH' ? 'Potassium Hydroxide' : 'Sodium Hydroxide', 0);
  }
  recipe.additives.forEach(item => add(INCI_NAMES[inciKey(item)] || item.name, additiveGrams(item)));
  return entries.sort((a, b) => b.grams - a.grams).map(entry => entry.name).join(', ');
};

// Label Template Component
const LabelTemplate = ({ 
  recipe, 
  batch, 
  brandName, 
  labelSize, 
  showQR, 
  showHindi, 
  showINCI,
  customTagline,
  batchNumber,
  weight,
  mfgDate,
  expDate,
}) => {
  const recipeName = recipe?.name || 'Artisan Soap';
  const hindiName = recipe?.hindiName || getHindiName(recipe?.name?.toLowerCase().replace(/\s+/g, '_'))?.romanized || '';
  const hindiScript = recipe?.hindiScript || getHindiName(recipe?.name?.toLowerCase().replace(/\s+/g, '_'))?.hindi || '';
  
  const inciList = formatINCI(recipe || {});
  
  const qrData = JSON.stringify({
    product: recipeName,
    batch: batchNumber || batch?.name,
    mfg: mfgDate,
    exp: expDate,
  });

  // Size configurations (in pixels for screen, will scale for print)
  const sizes = {
    small: { width: 200, height: 150, fontSize: 0.7 },
    medium: { width: 280, height: 200, fontSize: 0.85 },
    large: { width: 350, height: 250, fontSize: 1 },
    square: { width: 250, height: 250, fontSize: 0.9 },
  };
  
  const size = sizes[labelSize] || sizes.medium;

  return (
    <div 
      className="label-template"
      style={{
        width: size.width,
        height: size.height,
        border: '2px solid #333',
        borderRadius: 8,
        padding: 16,
        background: '#fff',
        fontFamily: 'Georgia, serif',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Brand Name */}
      <div style={{ 
        fontSize: `${1.2 * size.fontSize}em`, 
        fontWeight: 700, 
        textAlign: 'center',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: 4,
      }}>
        {brandName || 'ARTISAN SOAPS'}
      </div>

      {/* Product Name */}
      <div style={{ 
        fontSize: `${1.4 * size.fontSize}em`, 
        fontWeight: 600, 
        textAlign: 'center',
        marginBottom: showHindi ? 2 : 8,
        color: '#333',
      }}>
        {recipeName}
      </div>

      {/* Hindi Name */}
      {showHindi && (hindiScript || hindiName) && (
        <div style={{ 
          fontSize: `${0.85 * size.fontSize}em`, 
          textAlign: 'center',
          marginBottom: 8,
          color: '#666',
        }}>
          {hindiScript && <span style={{ fontFamily: 'sans-serif' }}>{hindiScript}</span>}
          {hindiScript && hindiName && ' • '}
          {hindiName && <span style={{ fontStyle: 'italic' }}>{hindiName}</span>}
        </div>
      )}

      {/* Tagline */}
      {customTagline && (
        <div style={{ 
          fontSize: `${0.7 * size.fontSize}em`, 
          textAlign: 'center',
          fontStyle: 'italic',
          color: '#666',
          marginBottom: 8,
        }}>
          {customTagline}
        </div>
      )}

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', gap: 10 }}>
        {/* Left side - Info */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* Weight */}
          <div style={{ 
            fontSize: `${1 * size.fontSize}em`, 
            fontWeight: 600,
            textAlign: 'center',
            marginBottom: 6,
          }}>
            {weight || '100'}g
          </div>

          {/* Properties/Benefits */}
          {recipe?.benefits && recipe.benefits.length > 0 && (
            <div style={{ 
              fontSize: `${0.6 * size.fontSize}em`, 
              textAlign: 'center',
              color: '#666',
              marginBottom: 6,
            }}>
              {recipe.benefits.slice(0, 2).join(' • ')}
            </div>
          )}
        </div>

        {/* Right side - QR */}
        {showQR && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RecipeQRCode data={qrData} size={labelSize === 'small' ? 64 : 90} />
          </div>
        )}
      </div>

      {/* INCI Ingredients */}
      {showINCI && (
        <div style={{ 
          fontSize: `${0.5 * size.fontSize}em`, 
          color: '#666',
          borderTop: '1px solid #e0e0e0',
          paddingTop: 6,
          marginTop: 6,
          lineHeight: 1.3,
        }}>
          <strong>Ingredients:</strong> {inciList.length > 150 ? inciList.slice(0, 150) + '...' : inciList}
        </div>
      )}

      {/* Footer - Dates & Batch */}
      <div style={{ 
        fontSize: `${0.55 * size.fontSize}em`, 
        color: '#666',
        display: 'flex',
        justifyContent: 'space-between',
        borderTop: '1px solid #e0e0e0',
        paddingTop: 6,
        marginTop: 6,
      }}>
        <span>Batch: {batchNumber || batch?.name || 'N/A'}</span>
        <span>Mfg: {mfgDate || 'N/A'}</span>
        <span>Exp: {expDate || 'N/A'}</span>
      </div>
    </div>
  );
};

// Main Label Generator Page
function LabelGenerator() {
  const [recipes, setRecipes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // Label options
  const [brandName, setBrandName] = useState('');
  const [customTagline, setCustomTagline] = useState('Handcrafted with Love');
  const [labelSize, setLabelSize] = useState('medium');
  const [showQR, setShowQR] = useState(true);
  const showHindi = false;  // Hindi names feature is disabled.
  const [showINCI, setShowINCI] = useState(true);
  const [batchNumber, setBatchNumber] = useState('');
  const [weight, setWeight] = useState('100');
  const [mfgDate, setMfgDate] = useState(new Date().toISOString().split('T')[0]);
  const [expDate, setExpDate] = useState('');
  const [printCount, setPrintCount] = useState(8);
  
  const printRef = useRef(null);

  // Load data
  useEffect(() => {
    const unsubs = [];
    try {
      unsubs.push(subscribeToUserCollection('recipes', (data) => {
        setRecipes(data);
      }));
      unsubs.push(subscribeToUserCollection('batches', (data) => {
        setBatches(data);
      }));
    } catch (error) {
      console.error('Error loading data:', error);
    }
    return () => unsubs.forEach(u => u());
  }, []);

  // Auto-fill from batch
  useEffect(() => {
    if (selectedBatch) {
      const batch = batches.find(b => b.id === selectedBatch);
      if (batch) {
        setBatchNumber(batch.name || selectedBatch.slice(-6));
        setSelectedRecipe(batch.recipeId || null);
        setMfgDate(batch.madeDate || '');
        setExpDate(batch.soapExpiryDate || '');
      }
    }
  }, [selectedBatch, batches]);

  const recipe = recipes.find(r => r.id === selectedRecipe);
  const batch = batches.find(b => b.id === selectedBatch);

  // Print function
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Labels</title>
          <style>
            @page { size: A4; margin: 10mm; }
            body { margin: 0; font-family: Georgia, serif; }
            .print-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10mm;
              padding: 5mm;
            }
            .label-template {
              page-break-inside: avoid;
              border: 2px solid #333 !important;
              border-radius: 8px;
              padding: 12px;
              background: #fff;
              box-sizing: border-box;
            }
          </style>
        </head>
        <body>
          <div class="print-grid">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <>
      <div className="sl-page labels-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Labels' }
          ]} />
          <div className="pageheading">Label Generator</div>
          <div className="pagedescription">Create professional product labels with QR codes</div>
        </div>

        <div className="labels-page-content borderleft">
          <section className="mypadding borderbottom">
            <div className="lbl-grid">
              {/* Left - Options */}
              <div>
                <div className="lbl-settings">
                  <h3 className="lbl-settings-title">Label settings</h3>

                  {/* Recipe & Batch Selection */}
                  <div className="lbl-form-row">
                    <div>
                      <label className="lbl-label">Recipe</label>
                      <select
                        className="lbl-select"
                        value={selectedRecipe || ''}
                        onChange={(e) => setSelectedRecipe(e.target.value)}
                      >
                        <option value="">— Select recipe —</option>
                        {recipes.map(r => (
                          <option key={r.id} value={r.id}>{r.name || 'Untitled'}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="lbl-label">Batch</label>
                      <select
                        className="lbl-select"
                        value={selectedBatch || ''}
                        onChange={(e) => setSelectedBatch(e.target.value)}
                      >
                        <option value="">— Select batch —</option>
                        {batches.map(b => (
                          <option key={b.id} value={b.id}>{b.name || 'Unnamed'}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Brand & Product Info */}
                  <div className="lbl-form-group">
                    <label className="lbl-label">Brand Name</label>
                    <input
                      type="text"
                      className="lbl-input"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="Your Brand Name"
                    />
                  </div>

                  <div className="lbl-form-group">
                    <label className="lbl-label">Tagline</label>
                    <input
                      type="text"
                      className="lbl-input"
                      value={customTagline}
                      onChange={(e) => setCustomTagline(e.target.value)}
                      placeholder="Handcrafted with Love"
                    />
                  </div>

                  <div className="lbl-form-row">
                    <div>
                      <label className="lbl-label">Weight (g)</label>
                      <input
                        type="text"
                        className="lbl-input"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="lbl-label">Batch Number</label>
                      <input
                        type="text"
                        className="lbl-input"
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)}
                        placeholder="e.g., LAV-2025-001"
                      />
                    </div>
                  </div>

                  <div className="lbl-form-row">
                    <div>
                      <label className="lbl-label">Mfg Date</label>
                      <input
                        type="date"
                        className="lbl-input"
                        value={mfgDate}
                        onChange={(e) => setMfgDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="lbl-label">Exp Date</label>
                      <input
                        type="date"
                        className="lbl-input"
                        value={expDate}
                        onChange={(e) => setExpDate(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Label Size */}
                  <div className="lbl-form-group">
                    <label className="lbl-label">Label Size</label>
                    <div className="lbl-size-options">
                      {['small', 'medium', 'large', 'square'].map(size => (
                        <button
                          key={size}
                          className={`lbl-size-btn${labelSize === size ? ' active' : ''}`}
                          onClick={() => setLabelSize(size)}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Toggles */}
                  <div className="lbl-toggles">
                    <label className="lbl-toggle">
                      <input type="checkbox" checked={showQR} onChange={(e) => setShowQR(e.target.checked)} />
                      <span>Show QR Code</span>
                    </label>
                    <label className="lbl-toggle">
                      <input type="checkbox" checked={showINCI} onChange={(e) => setShowINCI(e.target.checked)} />
                      <span>Show INCI Ingredients</span>
                    </label>
                    <small className="lbl-hint">Listed from heaviest to lightest. Check names and local labelling rules before printing.
                      {selectedRecipe?.recipeType === 'melt_pour' && ' For melt & pour, replace the base name with the INCI list your base supplier provides.'}</small>
                  </div>
                </div>

                {/* Print Options */}
                <div className="lbl-print-options">
                  <h3 className="lbl-print-title">Print options</h3>
                  <div className="lbl-form-group">
                    <label className="lbl-label">Labels per Page</label>
                    <select
                      className="lbl-select"
                      value={printCount}
                      onChange={(e) => setPrintCount(Number(e.target.value))}
                    >
                      <option value={4}>4 labels</option>
                      <option value={6}>6 labels</option>
                      <option value={8}>8 labels</option>
                      <option value={10}>10 labels</option>
                      <option value={12}>12 labels</option>
                    </select>
                  </div>
                  <button
                    className="lbl-print-btn"
                    onClick={handlePrint}
                    disabled={!recipe}
                  >
                    Print labels
                  </button>
                </div>
              </div>

              {/* Right - Preview */}
              <div>
                <h3 className="lbl-preview-title">Preview</h3>

                {!recipe ? (
                  <div className="lbl-preview-empty">
                    <div className="lbl-preview-empty-text">Select a recipe to preview label</div>
                  </div>
                ) : (
                  <div>
                    {/* Single Preview */}
                    <div className="lbl-preview-area">
                      <LabelTemplate
                        recipe={recipe}
                        batch={batch}
                        brandName={brandName}
                        labelSize={labelSize}
                        showQR={showQR}
                        showHindi={showHindi}
                        showINCI={showINCI}
                        customTagline={customTagline}
                        batchNumber={batchNumber}
                        weight={weight}
                        mfgDate={mfgDate}
                        expDate={expDate}
                      />
                    </div>

                    {/* Print Grid (hidden, for printing) */}
                    <div ref={printRef} style={{ display: 'none' }}>
                      {Array.from({ length: printCount }).map((_, i) => (
                        <LabelTemplate
                          key={i}
                          recipe={recipe}
                          batch={batch}
                          brandName={brandName}
                          labelSize={labelSize}
                          showQR={showQR}
                          showHindi={showHindi}
                          showINCI={showINCI}
                          customTagline={customTagline}
                          batchNumber={batchNumber}
                          weight={weight}
                          mfgDate={mfgDate}
                          expDate={expDate}
                        />
                      ))}
                    </div>

                    {/* Recipe Info */}
                    <div className="lbl-recipe-info">
                      <h4>Recipe Details</h4>
                      <div className="lbl-recipe-info-text">
                        <div><strong>Oils:</strong> {(recipe.oils || recipe.items || []).map(o => o.name).join(', ')}</div>
                        {recipe.additives?.length > 0 && (
                          <div><strong>Additives:</strong> {recipe.additives.map(a => a.name).join(', ')}</div>
                        )}
                        {recipe.superfat && (
                          <div><strong>Superfat:</strong> {recipe.superfat}%</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      <Footer />
      </div>
    </>
  );
}

export default LabelGenerator;
